import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReOS } from "../domain/store";
import type { InventoryItem } from "../domain/types";
import { reconcileWorkspaceCollectionRecords, type WorkspaceCollectionPage } from "../domain/workspaceCollections";
import { userSafeErrorMessage } from "../lib/userSafeError";

interface Result { key: string; records: InventoryItem[]; total: number; nextCursor?: string }

/** Keep loaded rows in place while fetching the next cursor; stale requests cannot cross cohorts. */
export function useInventoryCollection(fallback: InventoryItem[], filters: Record<string, string>) {
  const store = useReOS();
  const latest = useRef(store); latest.current = store;
  const key = `${store.workspace?.id}:${JSON.stringify(filters)}`;
  const [result, setResult] = useState<Result>();
  const resultRef = useRef(result); resultRef.current = result;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [retryVersion, setRetryVersion] = useState(0);
  const generation = useRef(0);
  const inFlight = useRef(false);
  const filtersRef = useRef(filters); filtersRef.current = filters;

  useEffect(() => {
    const requestFilters = { ...filtersRef.current };
    const version = ++generation.current;
    inFlight.current = true; setBusy(true); setError(undefined);
    const keepCount = resultRef.current?.key === key ? resultRef.current.records.length : 50;
    void (async () => {
      let cursor: string | undefined;
      const records = new Map<string, InventoryItem>();
      let page: WorkspaceCollectionPage<InventoryItem>;
      do {
        page = await latest.current.listWorkspaceCollection("inventory", { limit: 50, cursor, filters: requestFilters }) as WorkspaceCollectionPage<InventoryItem>;
        if (version !== generation.current) return;
        page.records.forEach((item) => records.set(item.id, item));
        cursor = page.nextCursor;
      } while (cursor && records.size < keepCount);
      setResult({ key, records: [...records.values()], total: page.total, nextCursor: cursor });
    })().catch((caught) => {
      if (version === generation.current) setError(userSafeErrorMessage(caught, "Couldn’t load inventory. Try again."));
    }).finally(() => {
      if (version === generation.current) { inFlight.current = false; setBusy(false); }
    });
    return () => { generation.current += 1; };
  }, [key, store.database, retryVersion]);

  const loadMore = useCallback(async () => {
    const current = resultRef.current;
    if (inFlight.current || current?.key !== key || !current.nextCursor) return;
    const version = generation.current;
    inFlight.current = true; setBusy(true); setError(undefined);
    try {
      const page = await latest.current.listWorkspaceCollection("inventory", { limit: 50, cursor: current.nextCursor, filters: filtersRef.current }) as WorkspaceCollectionPage<InventoryItem>;
      if (version !== generation.current) return;
      const records = new Map([...current.records, ...page.records].map((item) => [item.id, item]));
      setResult({ key, records: [...records.values()], total: page.total, nextCursor: page.nextCursor });
    } catch (caught) {
      if (version === generation.current) setError(userSafeErrorMessage(caught, "Couldn’t load more items. Try again."));
    } finally {
      if (version === generation.current) { inFlight.current = false; setBusy(false); }
    }
  }, [key]);

  const current = result?.key === key ? result : undefined;
  const records = useMemo(() => current ? reconcileWorkspaceCollectionRecords(current.records, fallback, store.inventoryItems) : fallback.slice(0, 50), [current, fallback, store.inventoryItems]);
  return { records, total: current ? Math.max(0, current.total - (current.records.length - records.length)) : fallback.length,
    loading: busy || (!current && !error), error, hasNext: Boolean(current?.nextCursor), loadMore,
    retry: () => setRetryVersion((value) => value + 1) };
}
